import OtpForm from '@/components/OtpForm';
import { ROUTES } from '@/constants/routes';
import { useInitiateAuth } from '@/hooks/requests/useAuth';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import useOtpForm from '@/hooks/useOtpForm';
import { OTPFormFields } from '@/types/common';
import { logAnalyticsEvent } from '@/utils/analytics';
import { formatLast4PhoneNumber } from '@/utils/phoneNumber';
import showToast from '@/utils/toast';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

export default function Otp() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { username } = state || {};
  const { isStaff, authUser, setAuthUser } = useAuthUser();
  const {
    initateAuth,
    authChallengeValidation,
    isLoadingInitateAuth,
    isLoadingAuthChallengeValidation,
  } = useInitiateAuth();
  const {
    formMethods,
    handleResendError,
    handleSubmitError,
    hasFailedOnce,
    setHasFailedOnce,
    submitForm,
  } = useOtpForm();

  if (!state) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  async function handleSubmit(data: OTPFormFields) {
    setHasFailedOnce(true);
    try {
      const response = await authChallengeValidation(
        {
          username: username.toLowerCase(),
          sms_mfa_code: data.code,
          session: authUser?.session || '',
        },
        {
          onError: () => handleSubmitError(true),
        },
      );

      if (!response.success) {
        logAnalyticsEvent({
          category: 'error',
          action: 'otpError',
        });
        handleSubmitError();
        return;
      }

      if (response.success && response.idToken) {
        logAnalyticsEvent({
          category: 'success',
          action: 'otpSuccess',
        });
        setAuthUser(response);
        queryClient.invalidateQueries();
        toast.dismiss();
        navigate(ROUTES.HOMEPAGE);
      }
    } catch {
      handleSubmitError();
    }
  }

  async function handleResendOtp() {
    const response = await initateAuth(
      { ...state, userGroup: isStaff ? 'staff' : 'teammate' },
      {
        onError: () => handleResendError(true),
      },
    );

    if (!response.success) {
      handleResendError();
      return;
    }

    setAuthUser(response);
    showToast({
      type: 'success',
      data: { content: 'A new code has been sent' },
    });
  }

  return (
    <OtpForm
      formMethods={formMethods}
      hasFailedOnce={hasFailedOnce}
      isLoading={isLoadingAuthChallengeValidation}
      isLoadingResend={isLoadingInitateAuth}
      content={`A verification code has been sent to your phone number ${formatLast4PhoneNumber(authUser?.challengePhone)}. Please enter it below.`}
      handleResendOtp={handleResendOtp}
      handleSubmit={handleSubmit}
      submitForm={submitForm}
    />
  );
}
